/**
 * Contextual Overlay Component
 * 
 * Provides contextual hints and explanations for different parts of the demo UI.
 */

import React, { useState, useEffect } from 'react';
import './ContextualOverlay.css';

interface ContextualOverlayProps {
  target: 'chat-input' | 'artifact-card' | 'context-section' | 'demo-controls';
  isVisible: boolean;
  onClose: () => void;
  position?: 'top' | 'bottom' | 'left' | 'right';
  title: string;
  content: string;
  actionHint?: string;
}

export const ContextualOverlay: React.FC<ContextualOverlayProps> = ({
  target,
  isVisible,
  onClose,
  position = 'bottom',
  title,
  content,
  actionHint
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      // Auto-hide after 8 seconds
      const timer = setTimeout(() => {
        onClose();
      }, 8000);
      
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`contextual-overlay contextual-overlay--${target} contextual-overlay--${position} ${isAnimating ? 'animating' : ''}`}
      role="tooltip"
      aria-label={`${title} - ${content}`}
    >
      <div className="overlay-content">
        <div className="overlay-header">
          <h4 className="overlay-title">{title}</h4>
          <button 
            className="overlay-close"
            onClick={onClose}
            aria-label="Close overlay"
          >
            ×
          </button>
        </div>
        
        <div className="overlay-body">
          <p className="overlay-description">{content}</p>
          {actionHint && (
            <div className="overlay-action">
              <span className="action-icon">💡</span>
              <span className="action-text">{actionHint}</span>
            </div>
          )}
        </div>
        
        <div className="overlay-arrow" />
      </div>
    </div>
  );
};

export default ContextualOverlay;
